import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  searchSuggest, 
  retrieveDetails, 
  reverseGeocode as mapboxReverseGeocode, 
  isMapboxConfigured,
  createAbortController,
  abortWithReason
} from '../services/mapboxService';
import { getOrCreateSessionToken, clearSessionToken } from '../utils/mapboxSessionTokenManager';

// Generate a request ID for race condition prevention
const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Default options for geolocation
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 5000,
  maximumAge: 300000, // 5 minutes
};

const normalizeHookError = (error, fallbackKey) => {
  if (error && typeof error === "object") {
    return {
      ...error,
      i18nKey:
        typeof error.i18nKey === "string" && error.i18nKey.trim()
          ? error.i18nKey
          : fallbackKey,
    };
  }

  return {
    code: "unknown",
    i18nKey: fallbackKey,
    message: String(error || ""),
  };
};

export function useMapboxSearch() {
  const { i18n } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [sessionToken, setSessionToken] = useState(() => getOrCreateSessionToken());
  
  // Refs for managing async operations - separate controllers for different operations
  const reverseGeocodeAbortRef = useRef(null);
  const searchSuggestAbortRef = useRef(null);
  const locationCacheTimeRef = useRef(null);
  
  // Request ID tracking to prevent race conditions
  const latestReverseGeocodeRequestRef = useRef(null);
  const latestSearchRequestRef = useRef(null);
  
  // Session token timer ref
  const sessionTokenTimerRef = useRef(null);
  
  // Reset session token after selection or timeout
  const resetSessionToken = useCallback(() => {
    clearSessionToken();
    const newToken = getOrCreateSessionToken();
    setSessionToken(newToken);
    // Clear any pending timer
    if (sessionTokenTimerRef.current) {
      clearTimeout(sessionTokenTimerRef.current);
      sessionTokenTimerRef.current = null;
    }
  }, []);
  
  // Schedule session token reset after user idle
  const scheduleSessionTokenReset = useCallback(() => {
    // Clear existing timer
    if (sessionTokenTimerRef.current) {
      clearTimeout(sessionTokenTimerRef.current);
    }
    // Set new timer for 5 minutes
    sessionTokenTimerRef.current = setTimeout(() => {
      resetSessionToken();
    }, 300000); // 5 minutes
  }, [resetSessionToken]);
  
  // Request user's geolocation
  const requestGeolocation = useCallback(() => {
    return new Promise((resolve) => {
      // Check if we have a cached location that's still valid
      if (userLocation && locationCacheTimeRef.current && 
          Date.now() - locationCacheTimeRef.current < 300000) { // 5 minutes
        resolve(userLocation);
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(location);
          locationCacheTimeRef.current = Date.now();
          resolve(location);
        },
        (error) => {
          console.log('Geolocation error:', error);
          resolve(null); // Silently fail
        },
        GEOLOCATION_OPTIONS
      );
    });
  }, [userLocation]);
  
  // Reverse geocoding for nearby places
  const reverseGeocode = useCallback(async (longitude, latitude) => {
    const requestId = generateRequestId();
    latestReverseGeocodeRequestRef.current = requestId;
    
    try {
      setLoading(true);
      setError(null);
      
      // Cancel any pending reverse geocode requests
      if (reverseGeocodeAbortRef.current) {
        abortWithReason(reverseGeocodeAbortRef.current, 'New reverse geocode request initiated');
      }
      reverseGeocodeAbortRef.current = createAbortController('Reverse geocode for nearby places');
      
      const results = await mapboxReverseGeocode(longitude, latitude, {
        language: i18n.language,
        abortSignal: reverseGeocodeAbortRef.current.signal
      });
      
      // Only update state if this is still the latest request
      if (requestId === latestReverseGeocodeRequestRef.current) {
        setSuggestions(results);
        return results;
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === latestReverseGeocodeRequestRef.current) {
        console.error('Reverse geocoding error:', error);
        setError(
          normalizeHookError(error, "records.editor.map.errors.nearby_failed")
        );
      }
      return [];
    } finally {
      if (requestId === latestReverseGeocodeRequestRef.current) {
        setLoading(false);
      }
    }
  }, [i18n.language]);
  
  // Search suggestions wrapper
  const searchSuggestWrapper = useCallback(async (query, proximity = null) => {
    const requestId = generateRequestId();
    latestSearchRequestRef.current = requestId;
    
    try {
      setLoading(true);
      setError(null);
      
      // Cancel any pending search suggest requests
      if (searchSuggestAbortRef.current) {
        abortWithReason(searchSuggestAbortRef.current, 'New search initiated');
      }
      searchSuggestAbortRef.current = createAbortController(`Search for: ${query}`);
      
      const options = {
        sessionToken,
        language: i18n.language,
        abortSignal: searchSuggestAbortRef.current.signal
      };
      
      // Add proximity bias if user location is available
      if (proximity) {
        options.proximity = `${proximity.lng},${proximity.lat}`;
      }
      
      const rawSuggestions = await searchSuggest(query, options);
      
      // Only update state if this is still the latest request
      if (requestId === latestSearchRequestRef.current) {
        setSuggestions(rawSuggestions);
        return rawSuggestions;
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === latestSearchRequestRef.current) {
        console.error('Search error:', error);
        setError(
          normalizeHookError(error, "records.editor.map.errors.search_failed")
        );
      }
      return [];
    } finally {
      if (requestId === latestSearchRequestRef.current) {
        setLoading(false);
      }
    }
  }, [i18n.language, sessionToken]);
  
  // Retrieve full details for a suggestion
  const retrieveDetailsWrapper = useCallback(async (suggestionId) => {
    try {
      return await retrieveDetails(suggestionId, sessionToken);
    } catch (error) {
      console.error('Retrieve details error:', error);
      setError(
        normalizeHookError(error, "records.editor.map.errors.retrieve_failed")
      );
      throw error;
    }
  }, [sessionToken]);
  
  
  // Initialize geolocation on mount
  useEffect(() => {
    requestGeolocation();
  }, [requestGeolocation]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reverseGeocodeAbortRef.current) {
        abortWithReason(reverseGeocodeAbortRef.current, 'Component unmounting');
      }
      if (searchSuggestAbortRef.current) {
        abortWithReason(searchSuggestAbortRef.current, 'Component unmounting');
      }
      if (sessionTokenTimerRef.current) {
        clearTimeout(sessionTokenTimerRef.current);
      }
    };
  }, []);
  
  
  return {
    suggestions,
    loading,
    error,
    userLocation,
    sessionToken,
    searchSuggest: searchSuggestWrapper,
    retrieveDetails: retrieveDetailsWrapper,
    reverseGeocode,
    requestGeolocation,
    resetSessionToken,
    scheduleSessionTokenReset,
    setSuggestions,
    isMapboxConfigured: isMapboxConfigured(),
  };
}
